import { DateTime } from 'luxon';
import logger from '../../utils/logging/logger';
import { hasCheckedInRecord } from '../../utils/attendance/attendanceSessionStatus';

// Helper to detect if an error is likely due to a deployment
export const isDeploymentRelatedError = (error) => {
  if (!error) return false;

  // Check for common deployment-related error patterns
  const errorMessage = error.message || error.toString() || '';
  const errorStatus = error.status || error.response?.status;

  // 503 Service Unavailable - common during deployments
  if (errorStatus === 503) return true;

  // Network errors during deployment
  if (errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('NetworkError') ||
      errorMessage.includes('Network request failed')) {
    return true;
  }

  // HTML returned instead of JSON (server restarting, returning error page)
  if (errorMessage.includes('Unexpected token') ||
      errorMessage.includes('SyntaxError') ||
      errorMessage.includes('<!DOCTYPE') ||
      errorMessage.includes('<html')) {
    return true;
  }

  // Chunk loading errors
  if (errorMessage.includes('Loading chunk') ||
      errorMessage.includes('ChunkLoadError')) {
    return true;
  }

  return false;
};

// Helper function to get session status
export const getSessionStatus = (session) => {
  // Checked-in state should always win, even after window closes
  if (hasCheckedInRecord(session)) {
    return 'checked-in';
  }

  // Show closed state only for students who did not check in
  if (
    session?.isPast ||
    session?.status === 'ended' ||
    session?.status === 'closed' ||
    session?.current_status === 'ended' ||
    session?.current_status === 'closed'
  ) {
    return 'ended';
  }

  return 'available';
};

export const getCheckedInTimeLabel = (session) => {
  const source = session.checked_in_at_utc || session.last_checkin;
  if (!source) return null;

  try {
    let checkedInTime;
    if (typeof source === 'string') {
      checkedInTime = DateTime.fromISO(source, { zone: 'utc' }).setZone('America/Chicago');
    } else {
      checkedInTime = DateTime.fromJSDate(source, { zone: 'utc' }).setZone('America/Chicago');
    }

    if (checkedInTime.isValid) {
      return checkedInTime.toFormat('h:mm a');
    }
  } catch (e) {
    logger.warn('Failed to parse check-in time', { source, error: e.message });
  }

  return null;
};

export const extractWeekNumber = (title) => {
  if (!title) return null;
  const match = String(title).match(/week\s*#?\s*(\d+)/i);
  return match ? Number(match[1]) : null;
};

export const getTimeRangeLabel = (session) => {
  const window = getCheckInWindowTimes(session);
  if (!window) return 'In progress now';
  return `${window.start.toFormat('h:mm a')} – ${window.end.toFormat('h:mm a')}`;
};

// Check-in window comes as full ISO timestamps (specific calendar dates), unlike the
// virtual session's time-of-day-only fields below.
const getCheckInWindowTimes = (session) => {
  if (!session.start_time || !session.end_time) return null;
  const start = DateTime.fromISO(session.start_time, { zone: 'utc' }).setZone('America/Chicago');
  const end = DateTime.fromISO(session.end_time, { zone: 'utc' }).setZone('America/Chicago');
  if (!start.isValid || !end.isValid) return null;
  return { start, end };
};

export const isCheckInWindowActive = (session) => {
  const window = getCheckInWindowTimes(session);
  if (!window) return false;
  const now = DateTime.now().setZone('America/Chicago');
  return now >= window.start && now <= window.end;
};

// Virtual session times are time-of-day only (no date), so "today" is always assumed -
// resolved to today's date in Chicago time, rolling the end past midnight if it's before start.
const getVirtualWindowToday = (session) => {
  if (!session.virtual_start_time || !session.virtual_end_time) return null;
  const now = DateTime.now().setZone('America/Chicago');
  const start = DateTime.fromFormat(String(session.virtual_start_time).slice(0, 5), 'HH:mm', { zone: 'America/Chicago' });
  const end = DateTime.fromFormat(String(session.virtual_end_time).slice(0, 5), 'HH:mm', { zone: 'America/Chicago' });
  if (!start.isValid || !end.isValid) return null;
  const startToday = now.set({ hour: start.hour, minute: start.minute, second: 0, millisecond: 0 });
  let endToday = now.set({ hour: end.hour, minute: end.minute, second: 0, millisecond: 0 });
  if (endToday <= startToday) endToday = endToday.plus({ days: 1 });
  return { now, startToday, endToday };
};

export const isLiveSessionActive = (session) => {
  const window = getVirtualWindowToday(session);
  if (!window) return false;
  return window.now >= window.startToday && window.now <= window.endToday;
};

const formatDurationParts = (totalSeconds) => {
  const total = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  if (hours > 0 || minutes > 0) parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
  parts.push(`${seconds} second${seconds === 1 ? '' : 's'}`);
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts[0]}, ${parts[1]}, and ${parts[2]}`;
};

export const getVirtualSessionWindowStatus = (session) => {
  const window = getVirtualWindowToday(session);
  if (!window) return { type: 'available', message: '' };
  const { now, startToday, endToday } = window;

  if (now < startToday) {
    return {
      type: 'before',
      message: `Virtual session opens in ${formatDurationParts(startToday.diff(now, 'seconds').seconds)} (on ${startToday.toFormat("EEE, MMM d 'at' h:mm a 'CT'")})`
    };
  }

  if (now > endToday) {
    return {
      type: 'after',
      message: `Virtual session window closed (ended ${endToday.toFormat("EEE, MMM d 'at' h:mm a 'CT'")})`
    };
  }

  return { type: 'available', message: '' };
};

// Compact "1h 32m" / "45m" / "58s" duration, for inline status text (as opposed to
// formatDurationParts' spelled-out "1 hour, 32 minutes, and 4 seconds" used in errors).
const formatShortDuration = (totalSeconds) => {
  const total = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${total}s`;
};

// Top-of-card live-class status (past/present/future), driven by the virtual session
// window. Only meaningful when the session actually has a virtual meeting attached.
export const getPrimaryStatus = (session) => {
  const window = getVirtualWindowToday(session);
  if (!window) return null;
  const { now, startToday, endToday } = window;
  const endLabel = endToday.toFormat('h:mm a');
  const startLabel = startToday.toFormat('h:mm a');

  if (now < startToday) {
    return {
      tone: 'upcoming',
      label: 'UPCOMING',
      description: `Starts in ${formatShortDuration(startToday.diff(now, 'seconds').seconds)} · begins ${startLabel}`,
      // Mobile drops the relative countdown and keeps just the clock-time boundary,
      // to fit the card's narrower top row alongside the status pill and week pill.
      shortDescription: `begins ${startLabel}`
    };
  }

  if (now > endToday) {
    const description = `Ended ${formatShortDuration(now.diff(endToday, 'seconds').seconds)} ago`;
    return { tone: 'ended', label: 'ENDED', description, shortDescription: description };
  }

  return {
    tone: 'live',
    label: 'LIVE NOW',
    description: `Started ${formatShortDuration(now.diff(startToday, 'seconds').seconds)} ago · ends ${endLabel}`,
    shortDescription: `ends ${endLabel}`
  };
};

// Bottom-of-card check-in status text (past/present/future), independent of the
// primary/virtual status above - checked-in always wins regardless of window state.
// remainingText is only populated while the window is actively open - it's shown as a
// separate emphasized chip on mobile (desktop folds it into secondaryText instead).
export const getCheckinDisplay = (session, isCheckedIn, isEnded) => {
  if (isCheckedIn) {
    const checkedInTime = getCheckedInTimeLabel(session);
    const primaryText = checkedInTime ? `Checked in at ${checkedInTime}` : 'Checked in';
    const secondaryText = "You're all set for this session.";
    return { primaryText, secondaryText, secondaryTextMobile: secondaryText, remainingText: null };
  }

  const window = getCheckInWindowTimes(session);
  if (!window) {
    return {
      primaryText: 'Check-in',
      secondaryText: 'No time restrictions',
      secondaryTextMobile: 'No time restrictions',
      remainingText: null
    };
  }

  const { start, end } = window;
  const startLabel = start.toFormat('h:mm a');
  const endLabel = end.toFormat('h:mm a');

  if (isEnded) {
    const secondaryText = `Window was ${startLabel} – ${endLabel}`;
    return { primaryText: 'Check-in closed', secondaryText, secondaryTextMobile: secondaryText, remainingText: null };
  }

  const now = DateTime.now().setZone('America/Chicago');

  if (now < start) {
    const secondaryText = `Opens in ${formatShortDuration(start.diff(now, 'seconds').seconds)}`;
    return { primaryText: `Check-in opens at ${startLabel}`, secondaryText, secondaryTextMobile: secondaryText, remainingText: null };
  }

  if (now > end) {
    const secondaryText = `Window was ${startLabel} – ${endLabel}`;
    return { primaryText: 'Check-in closed', secondaryText, secondaryTextMobile: secondaryText, remainingText: null };
  }

  return {
    primaryText: `Check in before ${endLabel}`,
    secondaryText: `Window opened at ${startLabel} · ${formatShortDuration(end.diff(now, 'seconds').seconds)} left`,
    secondaryTextMobile: `Open since ${startLabel}`,
    remainingText: `${formatShortDuration(end.diff(now, 'seconds').seconds)} left`
  };
};

export const getVirtualTimeRangeLabel = (session) => {
  const formatTime = (value) => {
    if (!value) return null;
    const parsed = DateTime.fromFormat(String(value).slice(0, 5), 'HH:mm');
    return parsed.isValid ? parsed.toFormat('h:mm a') : null;
  };
  const start = formatTime(session.virtual_start_time);
  const end = formatTime(session.virtual_end_time);
  if (start && end) return `${start} – ${end}`;
  if (start) return `Starts ${start}`;
  if (end) return `Ends ${end}`;
  return 'In progress now';
};
